import { Typography } from '@euler/app/components/typography';
import {
  ApdmImportAction,
  ApdmImportController,
} from '@euler/functions/apdm/import/useApdmImport';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { View } from 'react-native';
import { useTheme } from 'styled-components';
import { ReviewSection } from '../../ReviewControls';
import { ImportActions } from '../import-actions';
import { ImportProgress } from '../import-progress';
import { ImportQuestion } from '../import-question';
import { ImportSummary } from '../import-summary';
import { ImportStatusLine } from './ImportStatusLine';

/** One import: progress, its question, or its summary and what to do. */
export function ImportCard({
  controller,
  onDeleted,
}: {
  controller: ApdmImportController;
  onDeleted: () => void;
}) {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  const { state, pending, failure, actions } = controller;
  if (!state)
    return failure ? (
      <Typography.Text
        accessibilityRole="alert"
        size={rpx(13)}
        color={theme.colors.status.danger}
      >
        {failure}
      </Typography.Text>
    ) : null;
  return (
    <ReviewSection title={state.fileName}>
      <ImportStatusLine state={state} />
      {state.kind === 'working' ? <ImportProgress stage={state.stage} /> : null}
      {state.kind === 'question' ? (
        <ImportQuestion
          key={state.id}
          question={state.question}
          busy={pending === ApdmImportAction.Answer}
          onAnswer={answers => void actions.answer(answers)}
        />
      ) : null}
      {(state.kind === 'ready' ||
        state.kind === 'publishing' ||
        state.kind === 'published' ||
        state.kind === 'failed') &&
      state.summary ? (
        <ImportSummary key={state.id} summary={state.summary} />
      ) : null}
      {failure ? (
        <View style={{ marginTop: rpx(8) }}>
          <Typography.Text
            accessibilityRole="alert"
            size={rpx(13)}
            color={theme.colors.status.danger}
          >
            {failure}
          </Typography.Text>
        </View>
      ) : null}
      <ImportActions
        state={state}
        starting={pending === ApdmImportAction.Start}
        deleting={pending === ApdmImportAction.Delete}
        onStart={() => void actions.start()}
        onDelete={() => {
          void (async () => {
            if (await actions.remove()) onDeleted();
          })();
        }}
      />
    </ReviewSection>
  );
}
